import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchJson } from "../utils/fetchJson";
import {
  Button,
  Stack,
  Tabs,
  Tab,
  Card,
  Container,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { getPublishedArticles } from "../server/api";
import NewArticleCard from "../components/NewArticleCard";
import { findJournal } from "../utils/formatJournalPath";

// 处理文本中的URL并转换为超链接
const convertTextToLinks = (text) => {
  if (!text) return "";
  // 匹配URL的正则表达式
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  // 将URL替换为<a>标签
  return text.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary">${url}</a>`;
  });
};

const ArticleItem = ({ article }) => {
  const processedDesc = convertTextToLinks(article.desc);

  return (
    <Card className="mb-3 shadow-sm border-0">
      <Card.Body>
        <Card.Title className="mb-2">
          <a
            href={article.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-dark text-decoration-none hover:text-primary"
          >
            {article.name}
          </a>
        </Card.Title>
        <Card.Text
          className="text-muted small"
          dangerouslySetInnerHTML={{ __html: processedDesc }}
        />
      </Card.Body>
    </Card>
  );
};
const JournalPage = () => {
  const { journalId } = useParams();
  const [journal, setJournal] = useState(null);
  const [recentArt, setRecentArt] = useState(null);
  const [articles, setArticles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [limit] = useState(8); // 一页8条
  const [recentLimit] = useState(8);
  const [page, setPage] = useState(1);
  const [pageRecent, setPageRecent] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreRecent, setHasMoreRecent] = useState(true);

  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("articles");

  useEffect(() => {
    setLoading(true);
    fetchJson("/json/journals.json")
      .then((response) => response.json())
      .then((data) => {
        const foundJournal = findJournal(data.journals, journalId);
        setJournal(foundJournal || null);
        if (foundJournal) {
          handleRecent(foundJournal.title, 1);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading journals.json:", error);
        setLoading(false);
      });
  }, [journalId]);

  const handleTabSelect = async (key) => {
    setActiveTab(key);
    if (key === "cited" && !articles && journal) {
      // 当用户第一次切换到 Most Cited 时再加载
      await handleNomal(journal.title, 1);
    }
  };

  // 最多引用文章获取
  const handleNomal = async (jcr, pageNum = 1) => {
    try {
      const response = await getPublishedArticles({
        jcr: [jcr],
        page: pageNum,
        limit: limit,
        order: "cite",
        years: [],
        opt: "nomal",
      });
      const newArticles = response.data;
      if (pageNum === 1) {
        setArticles(newArticles);
      } else {
        setArticles((prev) => [...prev, ...newArticles]);
      }
      setHasMore(newArticles.length >= limit);
    } catch (error) {
      console.error("加载文章失败：", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecent = async (jcr, pageNum = 1) => {
    try {
      const response = await getPublishedArticles({
        jcr: [jcr],
        page: pageNum,
        limit: limit,
        order: "",
        years: [],
        opt: "nomal",
      });
      const newArticles = response.data;

      // 如果是第一页，直接覆盖；否则追加
      if (pageNum === 1) {
        setRecentArt(newArticles);
      } else {
        setRecentArt((prev) => [...prev, ...newArticles]);
      }

      // 如果返回的数据条数少于limit，说明没有更多了
      setHasMoreRecent(newArticles.length >= recentLimit);
    } catch (error) {
      console.error("加载最近文章失败：", error);
    } finally {
      setLoading(false);
    }
  };
  const handleShowMore = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    await handleNomal(journal.title, nextPage);
  };

  const handleShowMoreRecent = async () => {
    const nextPage = pageRecent + 1;
    setPageRecent(nextPage);
    await handleRecent(journal.title, nextPage);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-50">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!journal) {
    return (
      <div className="text-center py-12">
        <h3 className="text-muted mb-4">Journal data not found</h3>
        <Button variant="primary" href="/">
          Back Home
        </Button>
      </div>
    );
  }

  // 处理描述文本，转换URL为链接
  const processedDescription = convertTextToLinks(journal.desc);

  return (
    <Container className="journal-detail">
      {/* Journal Header Card */}
      <Card className="mb-6 shadow-sm border-0">
        <Card.Body>
          <Row className="align-items-start">
            {/* Journal Image */}
            <Col md={3} className="mb-4 mb-md-0">
              <div className="rounded overflow-hidden shadow-sm bg-light d-flex justify-center">
                {journal.img ? (
                  <img
                    alt={journal.title}
                    src={journal.img}
                    //   className="w-100 h-auto object-cover"
                    style={{ maxHeight: "330px" }}
                  />
                ) : (
                  <div
                    style={{
                      padding: "100px 0",
                      height: "330px",
                      textAlign: "center",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faImage}
                      size="3x"
                      className="image-icon"
                      style={{ color: "gray" }}
                    />
                    <div>{journal.title}</div>
                  </div>
                )}
              </div>

              <div className="d-grid gap-2 mt-4">
                {Boolean(journal.submit_url) ? (
                  <Button
                    variant="primary"
                    size="lg"
                    className="shadow-sm"
                    href={journal.submit_url}
                    target="_blank"
                  >
                    Submit Manuscript
                  </Button>
                ) : (
                  // 不存在时：不可点击（disabled）、无跳转属性、视觉上区分（可选改variant）
                  <Button
                    variant="secondary" // 可选：用次要样式区分“不可点击”状态
                    size="lg"
                    className="shadow-sm opacity-75" // 可选：加透明度增强不可点击视觉提示
                    disabled // 关键属性：禁用按钮，使其不可点击
                  >
                    Submit Manuscript
                  </Button>
                )}
                {journal.has_guide ? (
                  <Button
                    variant="primary"
                    size="lg"
                    className="shadow-sm"
                    href={`/journal/${journalId}/guide`}
                  >
                    Author Guidelines
                  </Button>
                ) : Boolean(journal.keai_url) ? (
                  <Button
                    variant="primary"
                    size="lg"
                    className="shadow-sm"
                    target="_blank"
                    href={journal.keai_url}
                  >
                    Author Guidelines
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="lg"
                    className="shadow-sm"
                    href="/guide"
                  >
                    Author Guidelines
                  </Button>
                )}

                {journal.id !== "jcgb" && (
                  Boolean(journal.edit_url) ? (
                    <Button
                      variant="primary"
                      size="lg"
                      className="shadow-sm"
                      target="_blank"
                      href={journal.edit_url}
                    >
                      Editorial Board
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="lg"
                      className="shadow-sm opacity-75"
                      disabled
                    >
                      Editorial Board
                    </Button>
                  )
                )}

                {journal.keai_home && (
                  <a href={journal.keai_home} target="_blank">
                    <Button
                      variant="primary"
                      size="lg"
                      className="shadow-sm"
                      style={{ width: "100%" }}
                    >
                      Journal homepage
                    </Button>
                  </a>
                )}
              </div>
            </Col>

            {/* Journal Information */}
            <Col md={9}>
              <div className="mb-4">
                <h1 className="mb-3">{journal.title}</h1>
                {journal.issn && (
                  <Stack
                    direction="horizontal"
                    gap={4}
                    className="mb-4 text-muted"
                  >
                    <span>ISSN: {journal.issn}</span>
                    {journal.impact && (
                      <>
                        <div className="vr" />
                        <span>
                          Impact Factor:{" "}
                          <strong className="text-dark">
                            {journal.impact}
                          </strong>
                        </span>
                      </>
                    )}
                  </Stack>
                )}
              </div>

              <div className="mb-4">
                <h3 className="h5 mb-3 text-secondary">About This Journal</h3>
                <div>
                  <div
                    className="text-muted"
                    dangerouslySetInnerHTML={{ __html: processedDescription }}
                  />
                  <div className="text-muted">
                    <div className="mt-2">
                      {journal.section && (
                        <div className="mt-2 mb-2">
                          {journal.section_subtitle?journal.section_subtitle:"This journal contains the following sections:"}
                        </div>
                      )}
                      {(isExpanded
                        ? Object.entries(journal.section)
                        : Object.entries(journal.section).slice(0, 1)
                      ).map(([key, value]) => (
                        <div key={key} className="mb-2">
                          {value}
                        </div>
                      ))}

                      {/* 只在条目数量超过3时显示切换按钮 */}
                      {Object.entries(journal.section).length > 1 && (
                        <button
                          className="btn btn-link text-muted p-0"
                          onClick={() => setIsExpanded(!isExpanded)}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? "Hide" : "Show More"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                {journal.editor_in_chief && (
                  <h3 className="h5 mb-3 text-secondary">Editor-in-Chief</h3>
                )}
                <div className="text-muted">{journal.editor_in_chief}</div>
              </div>
              <div className="text-muted">{journal.explain}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {/* Content Tabs */}
      <Tabs
        activeKey={activeTab}
        onSelect={handleTabSelect}
        transition={false}
        className="mb-6"
      >
        {/* 最新文章 Tab - 对应 recent 数据 */}
        <Tab eventKey="articles" title="Recent Articles" className="pt-4">
          {recentArt && (
            <div>
              {recentArt.map((article, index) => (
                <NewArticleCard key={index} article={article} />
              ))}
              {hasMoreRecent && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "20px",
                  }}
                >
                  <button
                    onClick={handleShowMoreRecent}
                    disabled={loading || !hasMoreRecent}
                    style={{
                      padding: "8px 20px",
                      border: "1px solid #003366",
                      borderRadius: "4px",
                      backgroundColor: loading ? "#5a7499" : "#003366",
                      color: "white",
                      cursor: loading ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {loading && (
                      <Spinner animation="border" size="sm" role="status" />
                    )}
                    {loading
                      ? "Loading..."
                      : hasMoreRecent
                      ? "Show more results"
                      : "No more results"}
                  </button>
                </div>
              )}
            </div>
          )}
          {!recentArt ||
            (recentArt.length === 0 && (
              <div className="d-flex justify-content-center py-8 text-muted">
                <p>No recent articles available.</p>
              </div>
            ))}
        </Tab>

        {/* 最多引用文章 Tab - 对应 most_cited 数据 */}
        <Tab
          eventKey="cited" // 修正eventKey与内容匹配
          title="Most Cited Articles "
          className="pt-4"
        >
          {articles && (
            <div>
              {articles.map((article, index) => (
                <NewArticleCard key={index} article={article} />
              ))}
              {hasMore && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "20px",
                  }}
                >
                  <button
                    onClick={handleShowMore}
                    disabled={loading || !hasMore}
                    style={{
                      padding: "8px 20px",
                      border: "1px solid #003366",
                      borderRadius: "4px",
                      backgroundColor: loading ? "#5a7499" : "#003366",
                      color: "white",
                      cursor: loading ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {loading && (
                      <Spinner animation="border" size="sm" role="status" />
                    )}
                    {loading
                      ? "Loading..."
                      : hasMore
                      ? "Show more results"
                      : "No more results"}
                  </button>
                </div>
              )}
            </div>
          )}
          {!articles ||
            (articles.length === 0 && (
              <div className="d-flex justify-content-center py-8 text-muted">
                <p>No most cited articles available.</p>
              </div>
            ))}
        </Tab>
      </Tabs>
    </Container>
  );
};

export default JournalPage;
